"use client";

import { addDays, format } from "date-fns";
import { createContext, useCallback, useContext, useMemo } from "react";
import { ICreateSavingsAgreement } from "@portal/types/api/ICreateSavingsAgreement";
import { IModifySavingsAgreementFund } from "@portal/types/api/IModifySavingsAgreementFund";
import { IModifySavingsAgreement } from "@portal/types/api/IModifySavingsAgreement";
import { GetInitialPeriodDayNumber } from "@portal/helpers/CalculateDates";
import { TextResources } from "@portal/assets/text/TextResources";
import { AllFundsHaveAnAmount, IsValidFundAmount } from "@portal/components/pages/agreements/shared/helpers/Validation";
import {
  ICreateSavingsAgreementForm,
  SavingsAgreementResponseObject,
  IFundAmount,
  ICreateSavingsAgreementContext,
} from "./types";
import usePersistState from "@portal/hooks/usePersistState";

const agreementData = {
  advisorCarriedOut: false,
  advisorName: "",
  adjustmentComment: "Min spareavtale",
  cashAccount: null,
  otherCashAccount: undefined,
  dateFrom: format(new Date(), "yyyy-MM-dd"),
  dateTo: null,
  monthTable: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  nextDate: format(addDays(new Date(), 5), "yyyy-MM-dd"),
  periodDayNumber: GetInitialPeriodDayNumber(),
  periodType: "M",
  savingsAgreementDescription: "",
  funds: [],
  isSavingRobot: false,
  yearlyIncreasePercent: 0,
} as IModifySavingsAgreement;

const initialRequest = {
  portfolioId: "",
  agreementData,
} as ICreateSavingsAgreement;

const initialResponse = {
  agreement: undefined,
} as SavingsAgreementResponseObject;

const createInitialState = () =>
  ({
    request: initialRequest,
    response: initialResponse,
    suitabilityReport: [] as string[],
    isLoading: false,
    error: "",
    step: 1,
    selectedFundsError: "",
    missingAmountError: "",
    totalAmountError: "",
  }) as ICreateSavingsAgreementContext;

export const CreateSavingsAgreementContext = createContext<ICreateSavingsAgreementForm | null>(null);

export function CreateSavingsAgreementProvider({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const [state, setState] = usePersistState<ICreateSavingsAgreementContext>("createSavingsAgreement", createInitialState());

  const IsValidRequest = useCallback(() => {
    return !!(
      state.request.agreementData.cashAccount &&
      state.request.portfolioId &&
      state.request.agreementData.advisorCarriedOut !== null
    );
  }, [state.request.agreementData.advisorCarriedOut, state.request.agreementData.cashAccount, state.request.portfolioId]);

  const value = useMemo<ICreateSavingsAgreementForm>(
    () => ({
      ...state,
      setSavingsAgreementMonthTable: (monthTable: number[]) => {
        setState(prev => ({
          ...prev,
          request: {
            ...prev.request,
            agreementData: { ...prev.request.agreementData, monthTable },
          },
        }));
      },
      setSavingsAgreementPortfolioId: (portfolioId: string) => {
        setState(prev => ({
          ...prev,
          request: { ...prev.request, portfolioId },
        }));
      },
      setSavingsAgreementName: (adjustmentComment: string) => {
        setState(prev => ({
          ...prev,
          request: {
            ...prev.request,
            agreementData: { ...prev.request.agreementData, adjustmentComment },
          },
        }));
      },
      setSavingsAgreementCashAccount: (cashAccount: string) => {
        setState(prev => ({
          ...prev,
          request: {
            ...prev.request,
            agreementData: { ...prev.request.agreementData, cashAccount },
          },
        }));
      },
      setSavingsAgreementOtherCashAccount: (otherCashAccount: string | undefined) => {
        setState(prev => ({
          ...prev,
          request: {
            ...prev.request,
            agreementData: { ...prev.request.agreementData, otherCashAccount },
          },
        }));
      },
      setSavingsAgreementAdvisorCarriedOut: (advisorCarriedOut: boolean) => {
        setState(prev => ({
          ...prev,
          request: {
            ...prev.request,
            agreementData: { ...prev.request.agreementData, advisorCarriedOut },
          },
        }));
      },
      setSavingsAgreementAdvisorName: (advisorName: string) => {
        setState(prev => ({
          ...prev,
          request: {
            ...prev.request,
            agreementData: { ...prev.request.agreementData, advisorName },
          },
        }));
      },
      setSavingsAgreementPeriodDayNumber: (periodDayNumber: number) => {
        setState(prev => ({
          ...prev,
          request: {
            ...prev.request,
            agreementData: { ...prev.request.agreementData, periodDayNumber },
          },
        }));
      },
      setSavingsAgreementPeriodType: (periodType: string) => {
        setState(prev => ({
          ...prev,
          request: {
            ...prev.request,
            agreementData: { ...prev.request.agreementData, periodType },
          },
        }));
      },
      setSavingsAgreementStartDate: (dateFrom: string) => {
        setState(prev => ({
          ...prev,
          request: {
            ...prev.request,
            agreementData: { ...prev.request.agreementData, dateFrom },
          },
        }));
      },
      setSavingsAgreementEndDate: (dateTo: string | null) => {
        setState(prev => ({
          ...prev,
          request: {
            ...prev.request,
            agreementData: { ...prev.request.agreementData, dateTo },
          },
        }));
      },
      setSavingsAgreementNextDate: (nextDate: string) => {
        setState(prev => ({
          ...prev,
          request: {
            ...prev.request,
            agreementData: { ...prev.request.agreementData, nextDate },
          },
        }));
      },
      addSavingsAgreementFund: (fund: IModifySavingsAgreementFund) => {
        setState(prev => {
          const updatedFunds = [...prev.request.agreementData.funds, fund];
          return {
            ...prev,
            request: {
              ...prev.request,
              agreementData: { ...prev.request.agreementData, funds: updatedFunds },
            },
            selectedFundsError: "",
          };
        });
      },
      setStep: (step: number) => {
        setState(prev => ({
          ...prev,
          step,
        }));
      },
      setSavingsAgreementFundAmount: ({ isin, amount }: IFundAmount) => {
        setState(prev => {
          const updatedFunds = prev.request.agreementData.funds.map(f => (f.isin === isin ? { ...f, amount } : f));
          const updatedAgreementData = { ...prev.request.agreementData, funds: updatedFunds };
          return {
            ...prev,
            request: {
              ...prev.request,
              agreementData: updatedAgreementData,
            },
            missingAmountError: AllFundsHaveAnAmount(updatedAgreementData) ? "" : TextResources.AGREEMENT_MISSING_AMOUNT,
            totalAmountError: IsValidFundAmount(updatedAgreementData) ? "" : TextResources.AGREEMENT_AMOUNT_BELOW_LIMIT,
          };
        });
      },
      removeSavingsAgreementFund: (isin: string) => {
        setState(prev => {
          const updatedFunds = prev.request.agreementData.funds.filter(f => f.isin !== isin);
          const updatedAgreementData = { ...prev.request.agreementData, funds: updatedFunds };
          return {
            ...prev,
            request: {
              ...prev.request,
              agreementData: updatedAgreementData,
            },
            selectedFundsError: updatedFunds.length > 0 ? "" : TextResources.AGREEMENT_MISSING_FUND,
            missingAmountError: AllFundsHaveAnAmount(updatedAgreementData) ? "" : TextResources.AGREEMENT_MISSING_AMOUNT,
            totalAmountError: IsValidFundAmount(updatedAgreementData) ? "" : TextResources.AGREEMENT_AMOUNT_BELOW_LIMIT,
          };
        });
      },
      removeSavingsAgreementFunds: () => {
        setState(prev => ({
          ...prev,
          request: {
            ...prev.request,
            agreementData: { ...prev.request.agreementData, funds: [] },
          },
        }));
      },
      setSavingsAgreementFunds: (funds: IModifySavingsAgreementFund[]) => {
        setState(prev => ({
          ...prev,
          request: {
            ...prev.request,
            agreementData: { ...prev.request.agreementData, funds },
          },
        }));
      },
      setSelectedFundsError: (selectedFundsError: string) => {
        setState(prev => ({
          ...prev,
          selectedFundsError,
        }));
      },
      setMissingAmountError: (missingAmountError: string) => {
        setState(prev => ({
          ...prev,
          missingAmountError,
        }));
      },
      setTotalAmountError: (totalAmountError: string) => {
        setState(prev => ({
          ...prev,
          totalAmountError,
        }));
      },
      clearHasError: () => {
        setState(prev => ({
          ...prev,
          error: "",
        }));
      },
      validate: () => {
        setState(prev => ({
          ...prev,
          selectedFundsError: prev.request.agreementData.funds.length > 0 ? "" : TextResources.AGREEMENT_MISSING_FUND,
          missingAmountError: AllFundsHaveAnAmount(prev.request.agreementData) ? "" : TextResources.AGREEMENT_MISSING_AMOUNT,
          totalAmountError: IsValidFundAmount(prev.request.agreementData) ? "" : TextResources.AGREEMENT_AMOUNT_BELOW_LIMIT,
        }));
      },
      setLoading: (isLoading: boolean) => {
        setState(prev => ({
          ...prev,
          isLoading,
        }));
      },
      setYearlyIncreasePercent: (yearlyIncreasePercent: number) => {
        setState(prev => ({
          ...prev,
          request: {
            ...prev.request,
            agreementData: { ...prev.request.agreementData, yearlyIncreasePercent },
          },
        }));
      },
      setResponse: (response: SavingsAgreementResponseObject) => {
        setState(prev => ({
          ...prev,
          response,
        }));
      },
      setSuitabilityReport: (suitabilityReport: string[]) => {
        setState(prev => ({
          ...prev,
          suitabilityReport,
        }));
      },
      clearSavingAgreementData: () => {
        setState(createInitialState());
      },
      IsValidRequest,
    }),
    [state, IsValidRequest, setState],
  );

  return <CreateSavingsAgreementContext.Provider value={value}>{children}</CreateSavingsAgreementContext.Provider>;
}

export const useCreateSavingsAgreement = (): ICreateSavingsAgreementForm => {
  const context = useContext(CreateSavingsAgreementContext);
  if (!context) {
    throw new Error("useCreateSavingsAgreement must be used within a CreateSavingsAgreementProvider");
  }

  return context;
};
